import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../../Card/Card';
import InputGroup from '../../../FormElements/InputGroup/InputGroup';
import MainSearchFieldsDragDrop from '../../../FormElements/MainSearchFields/MainSearchFieldsDragDrop';
import SearchDragDropFields from '../../../FormElements/SearchDragDropFields/SearchDragDropFields';
import SelectDropDown from '../../../FormElements/SelectDropDown/SelectDropDown';
import TabActionButtons from '../../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../../FormElements/ToggleSwitch/ToggleSwitch';
import TopSearchFieldsDragDrop from '../../../FormElements/TopSearchFields/TopSearchFieldsDragDrop';
import './TabDropdown.css';

const SearchPropertySettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  // Use a ref to track if initial load has happened
  const isInitialLoadRef = useRef(true);
  const isUpdatingFromParentRef = useRef(false);
  
  const [settings, setSettings] = useState({
    hvnly_searchBarTitle: __('Search Properties', 'havenlytics'),
    hvnly_searchButtonText: __('Search', 'havenlytics'),
    hvnly_hideTopSearchTitle: false,
    hvnly_hideTopSearchBar: false,
    hvnly_hideLeftSearchBar: false,
    hvnly_propertyViewType: 'GRID',
    hvnly_propertyOrderByType: 'TITLE',
    hvnly_propertySortByType: 'date',
    hvnly_propertyDepartmentOrderByType: 'RENT',
    hvnly_propertyThumbnailSize: 'thumbnail',
    hvnly_propertiesPerPage: 12,
    hvnly_enableAjaxLoadMore: true,
    hvnly_AjaxLoadMoreButtonText: __('Load More', 'havenlytics'),
    hvnly_search_fields: [],
    hvnly_top_search_fields: [],
    hvnly_main_search_fields: [],
  });

  // Helper function to convert value to boolean
  const toBoolean = useCallback((value) => {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1' || value === true) return true;
    if (value === 0 || value === '0' || value === false) return false;
    return !!value;
  }, []);

  // FIXED: Use useEffect with a flag to prevent circular updates
  useEffect(() => {
    // Skip if we're updating from parent to avoid loops
    if (isUpdatingFromParentRef.current) {
      isUpdatingFromParentRef.current = false;
      return;
    }
    
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      const searchPropertyFields = {
        hvnly_searchBarTitle: settingsData.hvnly_searchBarTitle !== undefined ? settingsData.hvnly_searchBarTitle : settings.hvnly_searchBarTitle,
        hvnly_searchButtonText: settingsData.hvnly_searchButtonText !== undefined ? settingsData.hvnly_searchButtonText : settings.hvnly_searchButtonText,
        hvnly_hideTopSearchTitle: settingsData.hvnly_hideTopSearchTitle !== undefined ? toBoolean(settingsData.hvnly_hideTopSearchTitle) : settings.hvnly_hideTopSearchTitle,
        hvnly_hideTopSearchBar: settingsData.hvnly_hideTopSearchBar !== undefined ? toBoolean(settingsData.hvnly_hideTopSearchBar) : settings.hvnly_hideTopSearchBar,
        hvnly_hideLeftSearchBar: settingsData.hvnly_hideLeftSearchBar !== undefined ? toBoolean(settingsData.hvnly_hideLeftSearchBar) : settings.hvnly_hideLeftSearchBar,
        hvnly_propertyViewType: settingsData.hvnly_propertyViewType !== undefined ? settingsData.hvnly_propertyViewType : settings.hvnly_propertyViewType,
        hvnly_propertyOrderByType: settingsData.hvnly_propertyOrderByType !== undefined ? settingsData.hvnly_propertyOrderByType : settings.hvnly_propertyOrderByType,
        hvnly_propertySortByType: settingsData.hvnly_propertySortByType !== undefined ? settingsData.hvnly_propertySortByType : settings.hvnly_propertySortByType,
        hvnly_propertyDepartmentOrderByType: settingsData.hvnly_propertyDepartmentOrderByType !== undefined ? settingsData.hvnly_propertyDepartmentOrderByType : settings.hvnly_propertyDepartmentOrderByType,
        hvnly_propertyThumbnailSize: settingsData.hvnly_propertyThumbnailSize !== undefined ? settingsData.hvnly_propertyThumbnailSize : settings.hvnly_propertyThumbnailSize,
        hvnly_propertiesPerPage: settingsData.hvnly_propertiesPerPage !== undefined && settingsData.hvnly_propertiesPerPage !== null 
            ? (typeof settingsData.hvnly_propertiesPerPage === 'number' 
                ? settingsData.hvnly_propertiesPerPage 
                : parseInt(settingsData.hvnly_propertiesPerPage, 10) || 12)
            : settings.hvnly_propertiesPerPage,
        hvnly_enableAjaxLoadMore: settingsData.hvnly_enableAjaxLoadMore !== undefined ? toBoolean(settingsData.hvnly_enableAjaxLoadMore) : settings.hvnly_enableAjaxLoadMore,
        hvnly_AjaxLoadMoreButtonText: settingsData.hvnly_AjaxLoadMoreButtonText !== undefined ? settingsData.hvnly_AjaxLoadMoreButtonText : settings.hvnly_AjaxLoadMoreButtonText,
        hvnly_search_fields: settingsData.hvnly_search_fields !== undefined ? settingsData.hvnly_search_fields : settings.hvnly_search_fields,
        hvnly_top_search_fields: settingsData.hvnly_top_search_fields !== undefined ? settingsData.hvnly_top_search_fields : settings.hvnly_top_search_fields,
        hvnly_main_search_fields: settingsData.hvnly_main_search_fields !== undefined ? settingsData.hvnly_main_search_fields : settings.hvnly_main_search_fields,
      };
      
      // Only update if there are actual changes
      const hasChanges = Object.keys(searchPropertyFields).some(key => 
        JSON.stringify(settings[key]) !== JSON.stringify(searchPropertyFields[key])
      );
      
      if (hasChanges) {
        setSettings(prev => ({ ...prev, ...searchPropertyFields }));
      }
    }
    isInitialLoadRef.current = false;
  }, [settingsData, toBoolean]);

  const handleInputChange = useCallback((field, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [field]: value };
      // Mark that we're updating from child to prevent circular updates
      isUpdatingFromParentRef.current = true;
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  }, [onSettingsChange]);

  const handleMainSearchFieldsChange = useCallback((fields) => {
    handleInputChange('hvnly_main_search_fields', fields);
  }, [handleInputChange]);

  const handleSearchFieldsChange = useCallback((fields) => {
    handleInputChange('hvnly_search_fields', fields);
  }, [handleInputChange]);

  const handleTopSearchFieldsChange = useCallback((fields) => {
    // Handle both array and object formats
    const fieldsToSave = Array.isArray(fields) ? fields : Object.values(fields);
    handleInputChange('hvnly_top_search_fields', fieldsToSave);
  }, [handleInputChange]);

  const handleSave = useCallback(async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  }, [settings, onSave]);

  const handleReset = useCallback(async () => {
    const defaultSettings = {
      hvnly_searchBarTitle: __('Search Properties', 'havenlytics'),
      hvnly_searchButtonText: __('Search', 'havenlytics'),
      hvnly_hideTopSearchTitle: false,
      hvnly_hideTopSearchBar: false,
      hvnly_hideLeftSearchBar: false,
      hvnly_propertyViewType: 'GRID',
      hvnly_propertyOrderByType: 'TITLE',
      hvnly_propertySortByType: 'date',
      hvnly_propertyDepartmentOrderByType: 'RENT',
      hvnly_propertyThumbnailSize: 'thumbnail',
      hvnly_propertiesPerPage: 12,
      hvnly_enableAjaxLoadMore: true,
      hvnly_AjaxLoadMoreButtonText: __('Load More', 'havenlytics'),
      hvnly_search_fields: [],
      hvnly_top_search_fields: [],
      hvnly_main_search_fields: [],
    };
    setSettings(defaultSettings);
    isUpdatingFromParentRef.current = true;
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset(defaultSettings);
      return true;
    }
    return false;
  }, [onSettingsChange, onReset]);

  const propertyViewOptions = [
    { value: 'SELECT', label: __('Select One', 'havenlytics') },
    { value: 'GRID', label: __('Grid View', 'havenlytics') },
    { value: 'LIST', label: __('List View', 'havenlytics') },
  ];
  
  const propertySortByOptions = [
    { value: 'a-to-z-title', label: __('A to Z (title)', 'havenlytics') },
    { value: 'z-to-a-title', label: __('Z to A (title)', 'havenlytics') },
    { value: 'date', label: __('Newest', 'havenlytics') },
    { value: 'date_oldest', label: __('Oldest', 'havenlytics') },
    { value: 'price_low', label: __('Price: Low to High', 'havenlytics') },
    { value: 'price_high', label: __('Price: High to Low', 'havenlytics') },
    { value: 'area', label: __('Square Footage', 'havenlytics') },
    { value: 'bedrooms', label: __('Number of Bedrooms', 'havenlytics') },
    { value: 'views', label: __('View Counts', 'havenlytics') },
    { value: 'rating', label: __('Rating', 'havenlytics') },
    { value: 'rand_property', label: __('Random Property', 'havenlytics') },
  ];

  // Ensure hvnly_top_search_fields is an array for the TopSearchFieldsDragDrop component
  const topSearchFieldsForComponent = Array.isArray(settings.hvnly_top_search_fields) 
    ? settings.hvnly_top_search_fields 
    : Object.values(settings.hvnly_top_search_fields || {});

  // Check if Top Search Bar is hidden
  const isTopSearchBarHidden = settings.hvnly_hideTopSearchBar === true;
  
  // Check if Left Search Filter Sidebar is hidden
  const isLeftSearchBarHidden = settings.hvnly_hideLeftSearchBar === true;

  return (
    <div 
      className='hvnly-settings-api__content hvnly-settings-api__content--active'
      id="search-property"
    >
      <h2>{ __('Search Property', 'havenlytics') }</h2>

      {/* Property Search Bar Card */}
      <Card title={__("Property Search Bar", "havenlytics")} icon="fas fa-search" collapsible>
        <InputGroup
          label={__("Search Properties", "havenlytics")}
          id="hvnly_searchBarTitle"
          type="text"
          value={settings.hvnly_searchBarTitle}
          onChange={(value) => handleInputChange("hvnly_searchBarTitle", value)}
        />
        <InputGroup
          label={__("Search Button Text", "havenlytics")}
          id="hvnly_searchButtonText"
          type="text"
          value={settings.hvnly_searchButtonText}
          onChange={(value) => handleInputChange("hvnly_searchButtonText", value)}
        />
      </Card>

      {/* AJAX Load More Card */}
      <Card title={__("AJAX Load More", "havenlytics")} icon="fas fa-sync-alt" collapsible>
        <ToggleSwitch
          id="hvnly_enableAjaxLoadMore"
          checked={settings.hvnly_enableAjaxLoadMore}
          onChange={(checked) => handleInputChange('hvnly_enableAjaxLoadMore', checked)}
          label={__("Enable AJAX Load More Button", "havenlytics")}
        />
        <div className="hvnly-settings-api__description" style={{ marginTop: '10px', marginBottom: '10px', fontSize: '12px', color: '#666' }}>
          {__("When enabled, properties will load via AJAX without page refresh using a \"Load More\" button. When disabled, traditional pagination will be used.", "havenlytics")}
        </div>
        <InputGroup
          label={__("Rename Ajax Load More Properties", "havenlytics")}
          id="hvnly_AjaxLoadMoreButtonText"
          type="text"
          value={settings.hvnly_AjaxLoadMoreButtonText}
          onChange={(value) => handleInputChange("hvnly_AjaxLoadMoreButtonText", value)}
        />
      </Card>

      {/* Hide Options Cards */}
      <Card title={__("Hide Top Search Page Title", "havenlytics")} icon="fas fa-heading" collapsible>
        <ToggleSwitch
          id="hvnly_hideTopSearchTitle"
          checked={settings.hvnly_hideTopSearchTitle}
          onChange={(checked) => handleInputChange('hvnly_hideTopSearchTitle', checked)}
          label={__("Hide Top Search Title", "havenlytics")}
        />
      </Card>

      <Card title={__("Hide Top Search Bar", "havenlytics")} icon="fas fa-search" collapsible>
        <ToggleSwitch
          id="hvnly_hideTopSearchBar"
          checked={settings.hvnly_hideTopSearchBar}
          onChange={(checked) => handleInputChange('hvnly_hideTopSearchBar', checked)}
          label={__("Hide Top Search Bar", "havenlytics")}
        />
        {/* Info message when Top Search Bar is hidden */}
        {isTopSearchBarHidden && (
          <div className="hvnly-settings-api__info-message" style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(255, 181, 7, 0.1)', borderLeft: '3px solid #FFB507', borderRadius: '4px', fontSize: '12px' }}>
            <i className="fas fa-info-circle"></i> {__('Main Search Fields and Top Search Fields are disabled because "Hide Top Search Bar" is enabled.', 'havenlytics')}
          </div>
        )}
      </Card>

      <Card title={__("Hide Left Search Filter Sidebar", "havenlytics")} icon="fas fa-filter" collapsible>
        <ToggleSwitch
          id="hvnly_hideLeftSearchBar"
          checked={settings.hvnly_hideLeftSearchBar}
          onChange={(checked) => handleInputChange('hvnly_hideLeftSearchBar', checked)}
          label={__("Hide Left Search Filter Sidebar", "havenlytics")}
        />
        {/* Info message when Left Search Sidebar is hidden */}
        {isLeftSearchBarHidden && (
          <div className="hvnly-settings-api__info-message" style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(255, 181, 7, 0.1)', borderLeft: '3px solid #FFB507', borderRadius: '4px', fontSize: '12px' }}>
            <i className="fas fa-info-circle"></i> {__('Manage Search Filter Fields is disabled because "Hide Left Search Filter Sidebar" is enabled.', 'havenlytics')}
          </div>
        )}
      </Card>

      {/* THREE COLUMN LAYOUT FOR SEARCH FIELDS */}
      <div className="hvnly-settings-api__three-columns">
        <div className="hvnly-settings-api__column">
          {/* Main Search Fields Card - Disabled when Top Search Bar is hidden */}
          <Card 
            title={__("Main Search Fields", "havenlytics")} 
            icon="fas fa-search" 
            collapsible
            disabled={isTopSearchBarHidden}
          >
            {isTopSearchBarHidden ? (
              <div className="hvnly-settings-api__disabled-message" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                <i className="fas fa-lock" style={{ marginRight: '8px' }}></i>
                {__('This section is disabled because "Hide Top Search Bar" is enabled.', 'havenlytics')}
              </div>
            ) : (
              <MainSearchFieldsDragDrop 
                showToast={showToast}
                onFieldsChange={handleMainSearchFieldsChange}
                initialFields={settings.hvnly_main_search_fields}
              />
            )}
          </Card>
        </div>
        
        <div className="hvnly-settings-api__column">
          {/* Top Search Fields Card - Disabled when Top Search Bar is hidden */}
          <Card 
            title={__("Top Search Fields", "havenlytics")} 
            icon="fas fa-layer-group" 
            collapsible
            disabled={isTopSearchBarHidden}
          >
            {isTopSearchBarHidden ? (
              <div className="hvnly-settings-api__disabled-message" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                <i className="fas fa-lock" style={{ marginRight: '8px' }}></i>
                {__('This section is disabled because "Hide Top Search Bar" is enabled.', 'havenlytics')}
              </div>
            ) : (
              <TopSearchFieldsDragDrop 
                showToast={showToast}
                onFieldsChange={handleTopSearchFieldsChange}
                initialFields={topSearchFieldsForComponent}
              />
            )}
          </Card>
        </div>

        <div className="hvnly-settings-api__column">
          {/* Drag & Drop Search Fields Card - Disabled when Left Search Sidebar is hidden */}
          <Card 
            title={__("Manage Search Filter Fields", "havenlytics")} 
            icon="fas fa-arrows-alt-v" 
            collapsible
            disabled={isLeftSearchBarHidden}
          >
            {isLeftSearchBarHidden ? (
              <div className="hvnly-settings-api__disabled-message" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                <i className="fas fa-lock" style={{ marginRight: '8px' }}></i>
                {__('This section is disabled because "Hide Left Search Filter Sidebar" is enabled.', 'havenlytics')}
              </div>
            ) : (
              <SearchDragDropFields 
                showToast={showToast}
                onFieldsChange={handleSearchFieldsChange}
                initialFields={settings.hvnly_search_fields}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Property View Types Card */}
      <Card title={__("Property View Types", "havenlytics")} icon="fas fa-th-large" collapsible>
        <SelectDropDown
          label={__("Default Property View", "havenlytics")}
          id="hvnly_propertyViewType"
          value={settings.hvnly_propertyViewType}
          options={propertyViewOptions}
          onChange={(value) => handleInputChange('hvnly_propertyViewType', value)}
        />
        <SelectDropDown
          label={__("Property Sort By", "havenlytics")}
          id="hvnly_propertySortByType"
          value={settings.hvnly_propertySortByType}
          options={propertySortByOptions}
          onChange={(value) => handleInputChange('hvnly_propertySortByType', value)}
        />
        
        <InputGroup
          label={__("Properties Per Page", "havenlytics")}
          info={__("Number of properties to display per page. Enter a number between 1 and 100.", "havenlytics")}
          id="hvnly_propertiesPerPage"
          type="number"
          min="1"
          max="100"
          step="1"
          value={settings.hvnly_propertiesPerPage}
          onChange={(value) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue) && numValue >= 1 && numValue <= 100) {
              handleInputChange("hvnly_propertiesPerPage", numValue);
            } else if (value === '') {
              handleInputChange("hvnly_propertiesPerPage", 12);
            }
          }}
        />
        
        <InputGroup
          label={__("Property Thumbnail Size", "havenlytics")}
          info={__("Enter image size ('thumbnail', 'medium', 'large', 'full') or custom size like '400x300'", "havenlytics")}
          id="hvnly_propertyThumbnailSize"
          type="text"
          value={settings.hvnly_propertyThumbnailSize}
          onChange={(value) => handleInputChange("hvnly_propertyThumbnailSize", value)}
        />
      </Card>

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Search Property Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default SearchPropertySettings;